import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Cookie, AlertOctagon } from 'lucide-react';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #000000;
  padding: 60px 20px;
  color: #ffffff;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: #111111;
  border: 1px solid #333333;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.5);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #333333;
  
  svg {
    color: #e0c16f;
    width: 32px;
    height: 32px;
  }
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #e0c16f;
  text-decoration: none;
  font-family: var(--font-heading);
  font-weight: 600;
  margin-bottom: 24px;
  transition: all 0.2s;
  
  &:hover {
    color: #bd851b;
  }
`;

const TextContent = styled.div`
  font-family: var(--font-body);
  color: #aaaaaa;
  line-height: 1.7;

  h2 {
    color: #ffffff;
    font-family: var(--font-heading);
    font-size: 1.5rem;
    margin: 32px 0 16px;
  }

  p {
    margin-bottom: 16px;
  }

  ul {
    margin-bottom: 16px;
    padding-left: 24px;
    
    li {
      margin-bottom: 8px;
    }
  }

  strong {
    color: #e0c16f;
  }
`;

// ==========================================
// 1. PRIVACY POLICY
// ==========================================
export const PrivacyPolicy = () => (
  <PageContainer>
    <ContentWrapper>
      <BackLink to="/"><ArrowLeft size={18} /> Back to Home</BackLink>
      <Header>
        <Shield />
        <Title>Privacy Policy</Title>
      </Header>
      <TextContent>
        <p>Last updated: {new Date().toLocaleDateString()}</p>
        <h2>1. Information We Collect</h2>
        <p>We collect information you provide directly to us when you register, including your username, email address, and payment information (handled securely via Stripe/PayPal). We do not store full credit card details on our servers.</p>
        
        <h2>2. How We Use Your Information</h2>
        <p>Your information is used to provide, maintain, and improve the BUCX platform. We use it to process transactions, send related information including confirmations and receipts, and detect/prevent fraudulent activity.</p>
        
        <h2>3. Information Sharing</h2>
        <p>We do not sell your personal data. We only share data with necessary third-party services (like payment processors) required to run the platform.</p>
      </TextContent>
    </ContentWrapper>
  </PageContainer>
);

// ==========================================
// 2. TERMS OF SERVICE (TOS)
// ==========================================
export const TermsOfService = () => (
  <PageContainer>
    <ContentWrapper>
      <BackLink to="/"><ArrowLeft size={18} /> Back to Home</BackLink>
      <Header>
        <FileText />
        <Title>Terms of Service</Title>
      </Header>
      <TextContent>
        <p>Last updated: {new Date().toLocaleDateString()}</p>
        <h2>1. Acceptance of Terms</h2>
        <p>By accessing and using BUCX, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform.</p>
        
        <h2>2. Creator Responsibilities</h2>
        <p>Creators are solely responsible for the content they publish. Content must not violate any laws, infringe on intellectual property, or contain explicit/harmful material. We reserve the right to suspend or terminate any account that violates these rules without prior notice.</p>
        
        <h2>3. Payments and Fees</h2>
        <p>BUCX facilitates payments between supporters and creators. We apply a standard platform fee to all transactions. We are not responsible for disputes regarding the quality of digital goods or services provided by creators.</p>
      </TextContent>
    </ContentWrapper>
  </PageContainer>
);

// ==========================================
// 3. COOKIE POLICY
// ==========================================
export const CookiePolicy = () => (
  <PageContainer>
    <ContentWrapper>
      <BackLink to="/"><ArrowLeft size={18} /> Back to Home</BackLink>
      <Header>
        <Cookie />
        <Title>Cookie Policy</Title>
      </Header>
      <TextContent>
        <p>Last updated: {new Date().toLocaleDateString()}</p>
        <h2>What Are Cookies</h2>
        <p>Cookies are small pieces of text sent to your web browser by a website you visit. They help the website remember information about your visit, like your authentication state and preferred language.</p>
        
        <h2>How We Use Cookies</h2>
        <ul>
          <li><strong>Essential Cookies:</strong> Required to authenticate users and prevent fraudulent use of user accounts.</li>
          <li><strong>Preferences:</strong> Used to remember your settings and theme preferences.</li>
          <li><strong>Analytics:</strong> Used to track platform performance and understand how users interact with BUCX.</li>
        </ul>
        <p>You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept essential cookies, you may not be able to use the platform.</p>
      </TextContent>
    </ContentWrapper>
  </PageContainer>
);

// ==========================================
// 4. REFUND POLICY (Strict)
// ==========================================
export const RefundPolicy = () => (
  <PageContainer>
    <ContentWrapper>
      <BackLink to="/"><ArrowLeft size={18} /> Back to Home</BackLink>
      <Header>
        <AlertOctagon />
        <Title>Refund Policy</Title>
      </Header>
      <TextContent>
        <p>Last updated: {new Date().toLocaleDateString()}</p>
        <h2>1. All Sales Are Final</h2>
        <p>Due to the nature of digital content, memberships, and direct creator support (tips/donations), <strong>all transactions made on BUCX are final and non-refundable.</strong></p>
        
        <h2>2. Digital Goods</h2>
        <p>Once a digital item is purchased and access is granted, it cannot be "returned." Therefore, we do not issue refunds for any shop purchases.</p>
        
        <h2>3. Donations & Tips</h2>
        <p>Tips and donations are voluntary gifts to creators to support their work. These are final and cannot be charged back or refunded once processed.</p>
        
        <h2>4. Fraudulent Transactions</h2>
        <p>The only exception to our strict no-refund policy is in the case of proven fraudulent transactions (e.g., unauthorized credit card use). In such cases, please contact your financial institution and our support team immediately.</p>
      </TextContent>
    </ContentWrapper>
  </PageContainer>
);